import axios from 'axios'
import authService from './authService'

// Configurazione axios
const api = axios.create({
    baseURL: '/api', // Usa proxy invece di URL diretto
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    }
})

// Intercettori per logging e autenticazione
api.interceptors.request.use(
    (config) => {
        console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`)

        // Aggiungi automaticamente il token JWT se presente
        const token = authService.getToken()
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }

        return config
    },
    (error) => {
        console.error('API Request Error:', error)
        return Promise.reject(error)
    }
)

api.interceptors.response.use(
    (response) => {
        console.log(`API Response: ${response.status} ${response.config.url}`)
        return response
    },
    (error) => {
        console.error('API Response Error:', error.response?.data || error.message)

        // Se ricevi 401 (Unauthorized), il token potrebbe essere scaduto o non valido
        if (error.response?.status === 401) {
            const currentPath = window.location.pathname
            // Evita loop infiniti se siamo già sulla pagina di login
            if (currentPath !== '/login') {
                console.warn('Token scaduto o non valido, reindirizzamento al login')
                authService.logout()
                // Reindirizza al login con la pagina corrente come redirect
                window.location.href = '/login?redirect=' + encodeURIComponent(currentPath)
            }
        }

        return Promise.reject(error)
    }
)

// Types
export interface ScrapingResult {
    title: string
    link?: string
    siteName: string
    sourceUrl: string
    scrapedAt: string
    lastModified?: string  // Data Last-Modified da HTTP header
    isModified: boolean  // True se il contenuto è cambiato
    changeDetectionMethod?: string  // "LastModified" o "ContentHash"
}

export interface CategorySummaryResult {
    siteName: string
    url: string
    firstResult?: ScrapingResult
    isSuccess: boolean
    errorMessage?: string
}

export interface CategorySummaryResponse {
    categoryName: string
    totalSites: number
    successfulSites: number
    sites: CategorySummaryResult[]
    generatedAt: string
}

export interface SiteDetailResponse {
    categoryName: string
    siteName: string
    siteUrl: string
    resultsCount: number
    results: ScrapingResult[]
    generatedAt: string
}

export interface AvailableCategoriesResponse {
    categories: string[]
    totalCategories: number
    generatedAt: string
}

export interface HealthResponse {
    status: string
    service: string
    timestamp: string
}

export interface ErrorResponse {
    error: string
    message: string
    timestamp: string
}

// API Functions
export const getCategories = () => {
    return api.get<AvailableCategoriesResponse>('/scraping/categories')
}

export const getCategorySummary = (categoryName: string) => {
    return api.get<CategorySummaryResponse>(`/scraping/category/${encodeURIComponent(categoryName)}/summary`)
}

export const getSiteDetail = (categoryName: string, siteName: string) => {
    // Debug encoding
    console.log('API getSiteDetail chiamata:', { categoryName, siteName })
    console.log('Encoded siteName:', encodeURIComponent(siteName))

    return api.get<SiteDetailResponse>(`/scraping/category/${encodeURIComponent(categoryName)}/site/${encodeURIComponent(siteName)}`)
}

export const getHealthCheck = () => {
    return api.get<HealthResponse>('/scraping/health')
}

export default api